import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, Eye, Archive, Download, Paperclip, Clock, Inbox, AlertTriangle, GraduationCap, BellRing } from "lucide-react";

const NOTICE = "Questa comunicazione e i relativi allegati rimarranno disponibili su Orbit per 3 giorni dall'invio. Scaricali se desideri conservarli.";

function TemporaryNotice() {
  return (
    <div data-testid="mail-view-notice" className="mb-4 rounded-xl border border-[#D9D4F0] bg-[#F4F3FA] px-4 py-3 flex items-start gap-2.5">
      <Clock className="h-4 w-4 text-[#6D5FB8] shrink-0 mt-0.5" />
      <p className="text-sm text-[#4A4470] leading-relaxed">{NOTICE}</p>
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

async function blobDownload(url, fallbackName) {
  const res = await api.get(url, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const name = m ? m[1] : fallbackName;
  const dl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a"); a.href = dl; a.download = name; a.click();
  window.URL.revokeObjectURL(dl);
}

async function blobOpen(url) {
  const res = await api.get(url, { responseType: "blob" });
  const obj = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/octet-stream" }));
  window.open(obj, "_blank", "noopener,noreferrer");
  setTimeout(() => window.URL.revokeObjectURL(obj), 60000);
}

export function ReadDialog({ item, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!item) return;
    api.get(`/office/mail-view/${item.id}`).then((r) => setDetail(r.data)).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore apertura anteprima");
    });
  }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mail-view-read-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading pr-6">{item?.subject}</DialogTitle>
          <DialogDescription>
            {item?.category_label} · {item?.recipients_count} destinatar{item?.recipients_count === 1 ? "io" : "i"} · {fmtDate(item?.sent_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#D9D4F0] bg-[#F4F3FA] px-3 py-2 text-xs text-[#4A4470] flex items-center gap-2">
          <Clock className="h-3.5 w-3.5 shrink-0" /> {item?.residual_label} · {NOTICE}
        </div>
        {detail?.recipients?.length > 0 && (
          <p className="text-xs text-slate-500 break-words"><b>A:</b> {detail.recipients.join(", ")}</p>
        )}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
          {detail ? (
            <iframe title="Anteprima mail" data-testid="mail-view-iframe" srcDoc={detail.html || "<p style='padding:16px;font-family:sans-serif'>Nessun contenuto</p>"}
              className="w-full h-[46vh] bg-white" sandbox="" />
          ) : (
            <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          )}
        </div>
        {detail?.attachments?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Allegati ({detail.attachments.length})</p>
            {detail.attachments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`mail-view-attachment-${i}`}>
                <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-700 truncate flex-1">{a.filename}</span>
                {a.available ? (
                  <>
                    <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`mail-view-att-open-${i}`}
                      onClick={() => blobOpen(`/office/mail-view/${item.id}/attachment/${i}`).catch(() => toast.error("Impossibile aprire l'allegato"))}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Apri
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid={`mail-view-att-download-${i}`}
                      onClick={() => blobDownload(`/office/mail-view/${item.id}/attachment/${i}`, a.filename).catch(() => toast.error("Download non riuscito"))}>
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : <span className="text-xs text-amber-600">Non più disponibile</span>}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end pt-1">
          <Button className="rounded-lg" data-testid="mail-view-read-download"
            onClick={() => blobDownload(`/office/mail-view/${item.id}/download`, "Orbit_Mail.zip").catch(() => toast.error("Download non riuscito"))}>
            <Download className="h-4 w-4 mr-1.5" /> Scarica tutto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MailCard({ item, onRead, onArchive, selectable, selected, onToggle }) {
  return (
    <div data-testid={`mail-view-card-${item.id}`}
      className={`rounded-xl border bg-white shadow-sm p-4 ${item.read ? "border-slate-200" : "border-[#C3B9E0]"}`}>
      <div className="flex items-start gap-3">
        {selectable && (
          <Checkbox checked={selected} onCheckedChange={onToggle} data-testid={`mail-view-select-${item.id}`} className="mt-1 shrink-0" />
        )}
        <div className="h-9 w-9 rounded-lg bg-[#EDEBF7] border border-[#D9D4F0] flex items-center justify-center shrink-0">
          {item.category === "check_assistenza" || item.category === "check_adm"
            ? <GraduationCap className="h-4 w-4 text-[#6D5FB8]" />
            : <Mail className="h-4 w-4 text-[#6D5FB8]" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm truncate ${item.read ? "font-medium text-slate-700" : "font-bold text-slate-900"}`}>{item.subject}</p>
            {!item.read && <span data-testid={`mail-view-unread-${item.id}`} className="h-2 w-2 rounded-full bg-[#6D5FB8] shrink-0" />}
            {item.simulated && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Simulazione Training</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {item.category_label} · {item.recipients_count} destinatar{item.recipients_count === 1 ? "io" : "i"}
            {item.attachments?.length ? ` · ${item.attachments.length} allegat${item.attachments.length === 1 ? "o" : "i"}` : ""}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(item.sent_at)}</p>
          <span data-testid={`mail-view-countdown-${item.id}`}
            className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#EDEBF7] border border-[#D9D4F0] px-2 py-0.5 text-[11px] font-semibold text-[#6D5FB8]">
            <Clock className="h-3 w-3" />{item.residual_label}
          </span>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="rounded-lg h-8 flex-1" data-testid={`mail-view-read-${item.id}`} onClick={() => onRead(item)}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Leggi
              </Button>
              {onArchive && (
                <Button size="sm" variant="outline" className="rounded-lg h-8 flex-1 text-[#6D5FB8] border-[#D9D4F0] hover:bg-[#EDEBF7]" data-testid={`mail-view-archive-${item.id}`} onClick={() => onArchive(item)}>
                  <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivia
                </Button>
              )}
            </div>
            <Button size="sm" variant="ghost" className="rounded-lg h-8 w-full text-slate-600" data-testid={`mail-view-download-${item.id}`}
              onClick={() => blobDownload(`/office/mail-view/${item.id}/download`, "Orbit_Mail.zip").catch(() => toast.error("Download non riuscito"))}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MailViewSection({ onChanged, refreshKey }) {
  const [tab, setTab] = useState("preview");
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(null);
  const [readItem, setReadItem] = useState(null);
  const [selected, setSelected] = useState([]);

  const loadPreview = useCallback(() => {
    api.get("/office/mail-view").then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
  }, []);
  const loadArchive = useCallback(() => {
    // L'apertura dell'Archivio marca lato backend le mail archiviate come "viste" (read_by):
    // ricarichiamo l'elenco e poi aggiorniamo il badge viola, che non le conteggia più.
    api.get("/office/mail-view/archive").then((r) => { setArchived(r.data?.items || []); onChanged && onChanged(); }).catch(() => setArchived([]));
  }, [onChanged]);

  // L'apertura della Mail View NON marca nulla come letto e NON azzera il badge (account reali).
  // Eccezione account DEMO: visualizzare Anteprime/Archivio azzera i badge non letti (gestita lato backend).
  useEffect(() => { loadPreview(); }, [loadPreview, refreshKey]);
  useEffect(() => { if (tab === "archivio") loadArchive(); }, [tab, loadArchive, refreshKey]);
  useEffect(() => {
    api.post("/office/mail-view/seen").then((r) => { if (r.data?.applied) onChanged && onChanged(); }).catch(() => {});
  }, [tab, onChanged]);

  const archiveOne = async (it) => {
    try {
      await api.post(`/office/mail-view/${it.id}/archive`);
      toast.success("Comunicazione archiviata");
      loadPreview(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const archiveAll = async () => {
    try {
      const r = await api.post("/office/mail-view/archive-all");
      toast.success(`${r.data?.archived || 0} comunicazioni archiviate`);
      loadPreview(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const downloadSelected = async () => {
    for (const id of selected) {
      try { await blobDownload(`/office/mail-view/${id}/download`, "Orbit_Mail.zip"); } catch { /* skip */ }
    }
  };

  const TabBtn = ({ k, children }) => (
    <button data-testid={`mail-view-tab-${k}`} onClick={() => setTab(k)}
      className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${tab === k ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
      {children}
    </button>
  );

  return (
    <div data-testid="mail-view-section">
      <div className="flex gap-2 mb-4">
        <TabBtn k="preview">Anteprime</TabBtn>
        <TabBtn k="archivio">Archivio</TabBtn>
      </div>
      <TemporaryNotice />

      {tab === "preview" ? (
        items === null ? <p className="text-sm text-slate-500">Caricamento…</p> :
        items.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="mail-view-empty">
            <Mail className="h-8 w-8 mx-auto mb-2 text-slate-300" />Nessuna mail in anteprima al momento.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {items.length >= 2 && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="rounded-lg text-[#6D5FB8] border-[#D9D4F0] hover:bg-[#EDEBF7]" data-testid="mail-view-archive-all" onClick={archiveAll}>
                  <Archive className="h-4 w-4 mr-1.5" /> Archivia tutto
                </Button>
              </div>
            )}
            {items.map((it) => (
              <MailCard key={it.id} item={it} onRead={setReadItem} onArchive={archiveOne} />
            ))}
          </div>
        )
      ) : (
        archived === null ? <p className="text-sm text-slate-500">Caricamento…</p> :
        archived.length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="mail-view-archive-empty">
            <Inbox className="h-8 w-8 mx-auto mb-2 text-slate-300" />Nessuna comunicazione archiviata disponibile.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {selected.length > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-[#D9D4F0] bg-[#F4F3FA] px-3 py-2">
                <span className="text-sm font-medium text-[#4A4470]">{selected.length} selezionate</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-lg h-8" data-testid="mail-view-archive-download-selected" onClick={downloadSelected}>
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica selezionate
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-lg h-8" onClick={() => setSelected([])}>Deseleziona</Button>
                </div>
              </div>
            )}
            {archived.map((it) => (
              <MailCard key={it.id} item={it} onRead={setReadItem} selectable selected={selected.includes(it.id)} onToggle={() => toggleSel(it.id)} />
            ))}
          </div>
        )
      )}

      <ReadDialog item={readItem} onClose={() => { setReadItem(null); loadPreview(); if (tab === "archivio") loadArchive(); onChanged && onChanged(); }} />
    </div>
  );
}

// Notifiche provvisorie ESTERNE alla Mail View: appaiono in cima alla pagina corrente
// finché l'Ufficio non agisce. Dopo un'azione la comunicazione passa nella gestione Mail View.
export function MailViewNotifications({ onChanged }) {
  const [items, setItems] = useState([]);
  const [readItem, setReadItem] = useState(null);

  const load = useCallback(() => {
    api.get("/office/mail-view/pending").then((r) => setItems(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const int = setInterval(load, 20000);
    return () => clearInterval(int);
  }, [load]);
  const afterAction = () => { load(); onChanged && onChanged(); };

  const archiveOne = async (it) => {
    try { await api.post(`/office/mail-view/${it.id}/archive`); toast.success("Spostata nell'Archivio Mail View"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
    afterAction();
  };
  const download = async (it) => {
    try { await blobDownload(`/office/mail-view/${it.id}/download`, "Orbit_Mail.zip"); await api.post(`/office/mail-view/${it.id}/acknowledge`); }
    catch (e) { toast.error("Download non riuscito"); }
    afterAction();
  };
  const archiveAll = async () => {
    try { const r = await api.post("/office/mail-view/pending/archive-all"); toast.success(`${r.data?.archived || 0} spostate nell'Archivio`); }
    catch (e) { toast.error("Operazione non riuscita"); }
    afterAction();
  };

  if (items.length === 0) return null;

  return (
    <div data-testid="mail-view-notifications" className="sticky top-0 z-40 px-4 md:px-6 pt-3">
      <div className="mx-auto max-w-5xl rounded-2xl border border-[#A99BD6] bg-[#EDEBF7] shadow-lg overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#C4B7E6] border-b border-[#A99BD6]">
          <div className="flex items-center gap-2 text-[#3B3466] font-heading font-bold text-sm">
            <BellRing className="h-4 w-4" />
            {items.length === 1 ? "Nuova comunicazione" : `${items.length} nuove comunicazioni`}
          </div>
          {items.length >= 2 && (
            <Button size="sm" variant="outline" className="rounded-lg h-8 bg-white/70 border-[#A99BD6] text-[#4A4470] hover:bg-white"
              data-testid="mail-view-notif-archive-all" onClick={archiveAll}>
              <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivia tutto
            </Button>
          )}
        </div>
        <div className="max-h-[45vh] overflow-y-auto divide-y divide-[#D9D4F0]">
          {items.map((it) => (
            <div key={it.id} data-testid={`mail-view-notif-${it.id}`} className="p-3">
              <div className="flex items-start gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-white border border-[#D9D4F0] flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-[#6D5FB8]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-slate-900 truncate">{it.subject}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{it.category_label}</span>
                    <span className="inline-flex items-center gap-1 text-[#6D5FB8] font-semibold"><Clock className="h-3 w-3" />{it.residual_label}</span>
                  </p>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="rounded-lg h-8 flex-1 bg-white"
                        data-testid={`mail-view-notif-read-${it.id}`} onClick={() => setReadItem(it)}>
                        <Eye className="h-3.5 w-3.5 mr-1.5" /> Leggi
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 flex-1 bg-white text-[#6D5FB8] border-[#D9D4F0]"
                        data-testid={`mail-view-notif-archive-${it.id}`} onClick={() => archiveOne(it)}>
                        <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivia
                      </Button>
                    </div>
                    <Button size="sm" variant="ghost" className="rounded-lg h-8 w-full text-slate-600"
                      data-testid={`mail-view-notif-download-${it.id}`} onClick={() => download(it)}>
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ReadDialog item={readItem} onClose={() => { setReadItem(null); afterAction(); }} />
    </div>
  );
}
